import { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  DollarSign,
  FileText,
  Package,
  Image,
  Link2,
  Search,
} from "lucide-react";
import toast from "react-hot-toast";

export default function ProductsServices() {
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("SAR");
  const [status, setStatus] = useState("Available");
  const [category, setCategory] = useState("");
  const [image, setImage] = useState(null);
  const [link, setLink] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);

  useEffect(() => {
    const fetchdata = async () => {
      const token = localStorage.getItem("token");

      try {
        const res = await fetch("https://zaichat.net/api/product", {
          headers: {
            auth: `Bearer ${token}`,
          },
        });

        if (res.status === 401) {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }

        const data = await res.json();
        setProducts(data.data);
      } catch (err) {
        toast.error("Failed to fetch products");
      }
    };

    fetchdata();
  }, []);

  const handleEditClick = (product) => {
    setEditingProductId(product.id);
    setName(product.name);
    setProductCode(product.code);
    setPrice(product.price);
    setCurrency(product.currency);
    setStatus(product.status ? "Available" : "Out of Stock");
    setCategory(product.category);
    setLink(product.link);
    setDescription(product.description);
    setNotes(product.notes);
    setImage(null);
    setIsEditModalOpen(true);
  };

  const handleUpdateProduct = async () => {
    if (!name || !price) {
      toast.error("Please enter product name and price");
      return;
    }

    const token = localStorage.getItem("token");

    const updatedData = {
      name,
      code: productCode || "",
      price: price.toString(),
      currency,
      status: status === "Available",
      category: category || "",
      link: link || "",
      discription: description || "",
      notes: notes || "",
      id: editingProductId,
    };

    try {
      const res = await fetch(`https://zaichat.net/api/update_product`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          auth: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }

      if (!res.ok) throw new Error("Failed to update product");

      setProducts(
        products.map((p) =>
          p.id === editingProductId ? { ...p, ...updatedData } : p
        )
      );

      setIsEditModalOpen(false);
      toast.success("✅ Product updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error("❌ Error updating product");
    }
  };

  const handleAddProduct = async () => {
    if (!name || !price) {
      toast.error("Please enter product name and price");
      return;
    }

    const token = localStorage.getItem("token");

    const formData = new FormData();
    formData.append("name", name);
    if (productCode) formData.append("code", productCode);
    formData.append("price", price);
    formData.append("currency", currency);
    formData.append("status", status === "Available");
    formData.append("category", category);
    if (link) formData.append("link", link);
    if (description) formData.append("description", description);
    if (notes) formData.append("notes", notes);
    if (image) formData.append("image", image);

    try {
      const res = await fetch("https://zaichat.net/api/save_product", {
        method: "POST",
        headers: {
          auth: `Bearer ${token}`,
        },
        body: formData,
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to save product");
      }

      const data = await res.json();
      setProducts([...products, data.data[0]]);

      // Reset form
      setName("");
      setProductCode("");
      setPrice("");
      setCurrency("SAR");
      setStatus("Available");
      setCategory("");
      setLink("");
      setDescription("");
      setNotes("");
      setImage(null);

      toast.success("Product saved successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Error saving product");
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch("https://zaichat.net/api/product", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          auth: `Bearer ${token}`,
        },
        body: JSON.stringify({ id }),
      });

      if (!res.ok) throw new Error("Failed to delete");

      setProducts(products.filter((p) => p.id !== id));
      toast.success("Product deleted successfully");
    } catch (err) {
      toast.error("Failed to delete product");
    }
  };

  const filteredProducts = products.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-cream/60 py-10 px-6 md:px-10">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-5xl md:text-6xl font-extrabold text-primary">
          Products & Services
        </h1>
        <p className="text-dark/70 mt-3 text-lg max-w-2xl mx-auto">
          Add and manage your products and services displayed by the chatbot.
        </p>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Add Product Form */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-accent/10 mb-8">
          <div className="flex items-center gap-2 mb-8">
            <Plus className="text-accent w-6 h-6" />
            <h2 className="text-2xl font-bold text-primary">
              Add Product or Service
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Product Name */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Product/Service Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Laptop XPS 15 or Design Service"
                className="w-full border-2 border-accent/20 rounded-xl p-3 text-sm focus:outline-none focus:border-accent transition-all"
              />
            </div>

            {/* Product Code */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Product Code / Supplier Code (Optional)
              </label>
              <input
                type="text"
                value={productCode}
                onChange={(e) => setProductCode(e.target.value)}
                placeholder="e.g., SKU-12345 or MOD-ABC"
                className="w-full border-2 border-accent/20 rounded-xl p-3 text-sm focus:outline-none focus:border-accent transition-all"
              />
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Price
              </label>
              <div className="flex items-center border-2 border-accent/20 rounded-xl px-3 py-2 focus-within:border-accent transition-all">
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full text-sm focus:outline-none bg-transparent"
                />
              </div>
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Currency
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full border-2 border-accent/20 rounded-xl p-3 text-sm focus:outline-none focus:border-accent transition-all"
              >
                <option>SAR (Saudi Riyal)</option>
                <option>USD (US Dollar)</option>
                <option>EUR (Euro)</option>
                <option>AED (Dirham)</option>
                <option>ILS (Shekel)</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border-2 border-accent/20 rounded-xl p-3 text-sm focus:outline-none focus:border-accent transition-all"
              >
                <option>Available</option>
                <option>Out of Stock</option>
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Category
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., Electronics, Design, Marketing..."
                className="w-full border-2 border-accent/20 rounded-xl p-3 text-sm focus:outline-none focus:border-accent transition-all"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Product Image (Optional)
              </label>
              <div className="flex items-center gap-3 border-2 border-accent/20 rounded-xl p-3 text-sm">
                <Image size={18} className="text-accent" />
                <input
                  type="file"
                  onChange={(e) => setImage(e.target.files[0])}
                  className="text-sm text-dark/70"
                />
              </div>
            </div>

            {/* Product Link */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">
                Product Link (Optional)
              </label>
              <div className="flex items-center gap-2 border-2 border-accent/20 rounded-xl p-3 text-sm focus-within:border-accent transition-all">
                <Link2 size={18} className="text-accent" />
                <input
                  type="text"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="https://example.com/product"
                  className="w-full text-sm focus:outline-none bg-transparent"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-primary mb-2">
              Description
            </label>
            <textarea
              rows="3"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Write a detailed description for this product..."
              className="w-full border-2 border-accent/20 rounded-xl p-3 text-sm focus:outline-none focus:border-accent transition-all"
            />
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-primary mb-2">
              Notes
            </label>
            <textarea
              rows="2"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              className="w-full border-2 border-accent/20 rounded-xl p-3 text-sm focus:outline-none focus:border-accent transition-all"
            />
          </div>

          {/* Add Button */}
          <div className="text-end">
            <button
              onClick={handleAddProduct}
              className="flex items-center gap-2 bg-gradient-to-r from-accent to-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all"
            >
              <Plus size={18} />
              Add to List
            </button>
          </div>
        </div>

        {/* Product Table */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-accent/10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-primary flex items-center gap-2">
                <FileText className="text-accent" /> Products & Services List
              </h2>
              <p className="text-dark/60 text-sm mt-1">
                Manage and view all your added products and services.
              </p>
            </div>

            <div className="flex items-center gap-2 border-2 border-accent/20 rounded-xl px-4 py-2 focus-within:border-accent transition-all">
              <Search size={18} className="text-dark/50" />
              <input
                type="text"
                placeholder="Search products..."
                className="text-sm focus:outline-none bg-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <Package size={48} className="mx-auto text-dark/30 mb-4" />
              <p className="text-dark/60 text-sm">
                No products or services yet. Add your first one above!
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-cream/50 text-primary font-semibold">
                    <th className="border border-accent/20 p-4 text-left">
                      Name
                    </th>
                    <th className="border border-accent/20 p-4 text-left">
                      Price
                    </th>
                    <th className="border border-accent/20 p-4 text-left">
                      Currency
                    </th>
                    <th className="border border-accent/20 p-4 text-left">
                      Category
                    </th>
                    <th className="border border-accent/20 p-4 text-center">
                      Status
                    </th>
                    <th className="border border-accent/20 p-4 text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-cream/30 transition-colors"
                    >
                      <td className="border border-accent/20 p-4 font-medium text-dark/90">
                        {item.name}
                      </td>
                      <td className="border border-accent/20 p-4 text-dark/80">
                        {item.price}
                      </td>
                      <td className="border border-accent/20 p-4 text-dark/70">
                        {item.currency}
                      </td>
                      <td className="border border-accent/20 p-4 text-dark/70">
                        {item.category || "—"}
                      </td>
                      <td className="border border-accent/20 p-4 text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            item.status === true
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {item.status ? "Available" : "Out of Stock"}
                        </span>
                      </td>
                      <td className="border border-accent/20 p-4">
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={() => handleEditClick(item)}
                            className="text-blue-600 hover:text-blue-800 transition-colors p-2 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-800 transition-colors p-2 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-6">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-primary mb-6 flex items-center gap-2">
              <Edit size={24} className="text-accent" />
              Edit Product
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Product Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Product Name"
                  className="w-full border-2 border-accent/20 rounded-xl p-3 focus:outline-none focus:border-accent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Product Code
                </label>
                <input
                  type="text"
                  value={productCode}
                  onChange={(e) => setProductCode(e.target.value)}
                  placeholder="Product Code"
                  className="w-full border-2 border-accent/20 rounded-xl p-3 focus:outline-none focus:border-accent transition-all"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-primary mb-2">
                    Price
                  </label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="Price"
                    className="w-full border-2 border-accent/20 rounded-xl p-3 focus:outline-none focus:border-accent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-2">
                    Currency
                  </label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full border-2 border-accent/20 rounded-xl p-3 focus:outline-none focus:border-accent transition-all"
                  >
                    <option>SAR (Saudi Riyal)</option>
                    <option>USD (US Dollar)</option>
                    <option>EUR (Euro)</option>
                    <option>AED (Dirham)</option>
                    <option>ILS (Shekel)</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-primary mb-2">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full border-2 border-accent/20 rounded-xl p-3 focus:outline-none focus:border-accent transition-all"
                  >
                    <option>Available</option>
                    <option>Out of Stock</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="Category"
                    className="w-full border-2 border-accent/20 rounded-xl p-3 focus:outline-none focus:border-accent transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Product Link
                </label>
                <input
                  type="text"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="https://example.com/product"
                  className="w-full border-2 border-accent/20 rounded-xl p-3 focus:outline-none focus:border-accent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                  rows="3"
                  className="w-full border-2 border-accent/20 rounded-xl p-3 focus:outline-none focus:border-accent transition-all"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes"
                  rows="2"
                  className="w-full border-2 border-accent/20 rounded-xl p-3 focus:outline-none focus:border-accent transition-all"
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-6 py-3 bg-gray-200 text-dark rounded-xl font-semibold hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateProduct}
                className="px-6 py-3 bg-gradient-to-r from-accent to-orange-500 text-white rounded-xl font-semibold hover:scale-105 transition-all"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-20 text-center text-dark/60 text-sm">
        Powered by <span className="text-accent font-semibold">Zuccess AI</span>
      </div>
    </div>
  );
}